// We need a token to be supplied to be able to query data
// If the user is logged in and has smtzAuth then we use it
// If the user is logged in but does nt have smtzAuth then we request it

import { setupServices } from "@/services";
import { dexieDB } from "@/services/dexie-database";
import { LogService } from "@/services/logging";
import AuthModule from "@/store/modules/auth";
import CashFloatModule from "@/store/modules/cash-float";
import ModalModule from "@/store/modules/modal";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { getSmtzAuth, getUserAccessToken } from "./helpers/auth";
import { initStoreModules, restoreAllStoreModules } from "./helpers/store";
import { SharedCallbacks } from "./shared-resources";
import qs from "qs-lite";
import { initializeOrderService } from "@/services/order.service";
import { initializePaymentService } from "@/services/payment.service";
import { initializeCustomFieldService } from "@/services/custom-field.service";
import { initPiniaStore } from "./helpers/pinia.helper";
import { initializeDiscountService } from "@/services/discount.service";
import {
  initializePackageLoggers,
  isVerboseLoggingEnabled,
} from "@/services/package-logger";

/**
 * Check if the current window is a customer display window.
 */
export function isCustomerDisplayWindow(): boolean {
  return window.location.pathname.includes("customer-display");
}

export function appSetup() {
  setupApollo();
  setupServices();
  initStoreModules();
  setupRequest();
  setupUserInfo();
  setupPOSConfig();
  setupUserLogout();
  setupModalSetStatus();
  setupCashfloat();
  setupResponse();
  setupConsoleLog();
  initializePackageLoggers();
  initializeDiscountService();
  initializeOrderService();
  initializePaymentService();
  initializeCustomFieldService();
  initPiniaStore();
}

/**
 * Setup for customer display window.
 * Same as appSetup() but with minimal setup:
 *  - no sync triggers, no watchEffects, no rxdb replication, etc.
 */
export function customerDisplaySetup() {
  // Only restore store state, no watchEffects for persistence
  restoreAllStoreModules();
  initializePackageLoggers();
  setupRequest();
  setupResponse();
  setupUserInfo();
  setupPOSConfig();
  setupUserLogout();
  setupModalSetStatus();

  // Open Dexie DB asynchronously (non-blocking)
  setupDexieDatabase(); // Used in Customer Display
}

/**
 * Opens Dexie database asynchronously.
 * Called after synchronous setup to avoid blocking route guards.
 */
async function setupDexieDatabase() {
  try {
    if (!dexieDB.isOpen()) {
      await dexieDB.open();
    }
  } catch (error) {
    LogService.info("Error opening Dexie DB", error);
  }
}

function setupConsoleLog() {
  const originalLog = console.log;
  console.log = (...args: any[]): void => {
    if (!isVerboseLoggingEnabled()) {
      return;
    }
    originalLog.apply(console, args);
  };
}

export function setupApollo() {
  SharedCallbacks.gqlAuthHeader = getSmtzAuth;
}

export function setupRequest() {
  SharedCallbacks.authHeaderCallback = authBuilder;
  SharedCallbacks.startTimeMetadataCallback = startTimeMetadataBuilder;
}

export function setupResponse() {
  SharedCallbacks.monitorLatencyCallback = getResponseLatency;
}

export function authBuilder(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  return new Promise((resolve, reject) => {
    const authorization = config.headers?.Authorization;
    const cookieToken = getUserAccessToken();

    if (AuthModule.token || cookieToken) {
      config.headers.set("Authorization", authorization || String(cookieToken));
      resolve(config);
    } else {
      reject(new Error("No token found in AuthModule"));
    }
  });
}

export function startTimeMetadataBuilder(config: InternalAxiosRequestConfig) {
  // Capture the request time and store it in the request metadata
  (config as any).metadata = { requestStartTime: Date.now() };
}

export function getResponseLatency(
  response: AxiosResponse,
  error?: AxiosError
) {
  // Calculate the latency
  // make sure that we don't log the latency when the base url is the log server to avoid an infinite loop
  if (
    (response.config as any).metadata?.requestStartTime &&
    response.config.baseURL !== import.meta.env.VITE_LOG_API
  ) {
    const requestStartTime = (response.config as any).metadata.requestStartTime;
    const latency = Date.now() - requestStartTime;
    const method = (response.config.method || "UNKNOWN").toUpperCase();
    const queryString = response.config.params
      ? `?${qs.stringify(response.config.params)}`
      : "";
    const url = response.config.url + queryString;

    if (error) {
      LogService.info(
        `${method} Request for ${url} failed after ${latency}ms`,
        { error }
      );
    } else {
      LogService.info(`${method} Request for ${url} took ${latency}ms`);

      // Monitoring Adyen Terminal slow connectivity calls
      if (
        url.includes("connected-terminals?uniqueTerminalId") &&
        latency > 1000
      ) {
        // Lets throw an alert
        const match = url.match(/uniqueTerminalId=[^&]+/);
        LogService.info(
          `Slow Terminal Connectivity Captured (${latency}ms): ${
            match ? match[0] : "terminalId Not Found"
          }`
        );
      }
    }
  }
}

export function setupUserInfo() {
  SharedCallbacks.getSellerId = () => {
    const sellerId = UserModule.userId;
    if (!sellerId) {
      throw new Error("No seller id on app");
    }

    return sellerId;
  };
}

export function setupPOSConfig() {
  SharedCallbacks.getPOSConfigId = () => {
    const posConfig = POSConfigModule.selectedConfig;
    if (!posConfig) {
      throw new Error("No POS Config set");
    }

    return posConfig.id;
  };

  SharedCallbacks.getGroupInfo = () => {
    const groupInfo = POSConfigModule.groupInfo;
    if (!groupInfo) {
      throw new Error("No group info set");
    }

    return groupInfo;
  };
}

export function setupUserLogout() {
  SharedCallbacks.UserLogOut = AuthModule.UserLogOut;
}

export function setupModalSetStatus() {
  SharedCallbacks.ModalSetStatus = ModalModule.setStatus;
}

export function setupCashfloat() {
  SharedCallbacks.cashfloat = () => {
    const cashfloat = CashFloatModule.cashFloat;
    if (!cashfloat) {
      return null;
    }

    return cashfloat;
  };
}
